import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import * as Icons from 'lucide-react'
import { ArrowLeft, Users, Crown, Upload, Edit3, LogOut, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { teamsAPI, composAPI, profileItemsAPI, authAPI } from '@/lib/api'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/hooks/useAuth'
import {
  ProfileHero, StatStrip, TrophyHall, MatchLog, SectionHeader, Avatar,
} from '@/components/profile/ProfileFrame'
import { rarityColor } from '@/lib/rarity'

/* Team-profielpagina — zelfde HUD-taal als de speler-page. Signature = de
   corner-brackets rond de hero, hier gekleurd in de team-primary_color. */

function icoonVan(naam) { return Icons[naam] || Users }

export default function TeamProfilePage() {
  const { teamId } = useParams()
  const { user: currentUser, isAdmin } = useAuth()
  const [detail, setDetail] = useState(null)      // { team, members, loadout }
  const [compo, setCompo]   = useState([])
  const [inventaris, setInventaris] = useState(null)
  const [loadoutOpen, setLoadoutOpen] = useState(false)
  const [invites, setInvites] = useState({ myInvite: null, myRequest: null, requests: [] })
  const [err, setErr]       = useState(null)
  const fileRef = useRef()
  const bannerRef = useRef()

  const laad = useCallback(() => {
    teamsAPI.get(teamId).then(r => setDetail(r.data)).catch(e => setErr(e.response?.data?.error ?? e.message))
    composAPI.listResults({ limit: 200 })
      .then(r => setCompo((r.data?.results ?? []).filter(x =>
        x.meta?.persistent_team_id === Number(teamId) ||
        x.team_name === detail?.team?.name    // fallback voor rijen zonder team_id-meta
      )))
      .catch(() => {})
  }, [teamId, detail?.team?.name])

  useEffect(() => { laad() }, [teamId])
  useEffect(() => {
    if (!detail?.team) return
    composAPI.listResults({ limit: 200 })
      .then(r => setCompo((r.data?.results ?? []).filter(x =>
        x.meta?.persistent_team_id === Number(teamId) || x.team_name === detail.team.name
      )))
      .catch(() => {})
  }, [detail?.team?.id, detail?.team?.name, teamId])

  // Invites & requests: pending invite voor mij op dit team, mijn eigen open request,
  // en (als kapitein) alle pending requests.
  const laadInvites = useCallback(async () => {
    if (!currentUser) return
    try {
      const [mine, reqs] = await Promise.allSettled([
        teamsAPI.myInvites(),
        (isAdmin || detail?.members?.some(m => m.user_id === currentUser?.id && m.role === 'captain'))
          ? teamsAPI.teamRequests(teamId) : Promise.resolve({ data: { requests: [] } }),
      ])
      const alle = mine.status === 'fulfilled' ? (mine.value.data?.invites ?? []) : []
      const mijnRequests = mine.status === 'fulfilled' ? (mine.value.data?.myRequests ?? []) : []
      const myInvite = alle.find(i => i.team_id === Number(teamId)) || null
      const myRequest = mijnRequests.find(r => r.team_id === Number(teamId)) || null
      setInvites({
        myInvite,
        myRequest,
        requests: reqs.status === 'fulfilled' ? (reqs.value.data?.requests ?? []) : [],
      })
    } catch {}
  }, [currentUser?.id, teamId, isAdmin, detail?.members])
  useEffect(() => { if (detail?.team) laadInvites() }, [detail?.team?.id, laadInvites])

  if (err) return <p className="text-sm text-signal">{err}</p>
  if (!detail?.team) return <p className="text-sm text-muted-foreground">Laden…</p>

  const { team, members, loadout } = detail
  const kanBeheren = isAdmin || members.some(m => m.user_id === currentUser?.id && m.role === 'captain')
  const benLid = members.some(m => m.user_id === currentUser?.id)
  const Icon = icoonVan(team.icon_name)
  // Equipped accent > team.primary_color als default.
  const accent = loadout?.accent?.config?.color || team.primary_color || '#B4FF39'

  const goud = compo.filter(r => r.place === 1).length
  const zilver = compo.filter(r => r.place === 2).length
  const brons = compo.filter(r => r.place === 3).length

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try { await teamsAPI.uploadLogo(team.id, file); laad() }
    catch (err) { window.alert(err.response?.data?.error ?? err.message) }
    finally { if (fileRef.current) fileRef.current.value = '' }
  }
  const uploadBanner = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { window.alert('Max 5MB'); return }
    try { await teamsAPI.uploadBanner(team.id, file); laad() }
    catch (err) { window.alert(err.response?.data?.error ?? err.message) }
    finally { if (bannerRef.current) bannerRef.current.value = '' }
  }
  const wisBanner = async () => {
    if (!window.confirm('Team-banner verwijderen?')) return
    try { await teamsAPI.deleteBanner(team.id); laad() }
    catch (err) { window.alert(err.response?.data?.error ?? err.message) }
  }

  const wisLid = async (userId) => {
    if (!window.confirm('Uit team halen?')) return
    try { await teamsAPI.removeMember(team.id, userId); laad() }
    catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  const wisselKapitein = async (userId, huidig) => {
    const nieuw = huidig === 'captain' ? 'member' : 'captain'
    try { await teamsAPI.setRole(team.id, userId, nieuw); laad() }
    catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }

  return (
    <div>
      <Button asChild variant="ghost" size="sm" className="mb-3">
        <Link to="/teams"><ArrowLeft className="h-4 w-4 mr-1" />Alle teams</Link>
      </Button>

      <ProfileHero
        accent={accent}
        banner={loadout?.banner}
        customBannerUrl={team.banner_url}
        bannerSource={team.banner_source}
        eyebrow="Team"
        name={team.name}
        handle={team.description || null}
        meta={
          <div className="pf-mono text-[11px] uppercase tracking-widest">
            {members.length} {members.length === 1 ? 'lid' : 'leden'} ·
            {' '}aangemaakt {new Date(team.created_at).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        }
        avatar={
          <div className="relative">
            {team.logo_url
              ? <Avatar url={team.logo_url} name={team.name} size={128} accent={accent} frame={loadout?.frame} shape="square" />
              : <div className="h-32 w-32 rounded-[10px] grid place-items-center shrink-0"
                     style={{ padding: 3, background: `linear-gradient(135deg, ${accent}, transparent 50%)` }}>
                  <div className="w-full h-full rounded-[8px] grid place-items-center bg-background">
                    <Icon className="h-16 w-16" style={{ color: accent }} />
                  </div>
                </div>}
            {kanBeheren && (
              <>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                <button type="button" onClick={() => fileRef.current?.click()}
                        className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-card border-2 border-border grid place-items-center hover:bg-acid/10 hover:border-acid transition-colors"
                        title="Nieuw logo uploaden">
                  <Upload className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        }
        right={
          <div className="flex gap-2 flex-wrap">
            {!benLid && invites.myInvite && (
              <>
                <Button size="sm"
                        onClick={async () => {
                          try { await teamsAPI.respondInvite(invites.myInvite.id, true); laad(); laadInvites() }
                          catch (e) { setErr(e.response?.data?.error ?? e.message) }
                        }}>
                  <Icons.Check className="h-4 w-4 mr-1" /> Aanvaard uitnodiging
                </Button>
                <Button variant="outline" size="sm"
                        onClick={async () => {
                          try { await teamsAPI.respondInvite(invites.myInvite.id, false); laadInvites() }
                          catch (e) { setErr(e.response?.data?.error ?? e.message) }
                        }}>Weiger</Button>
              </>
            )}
            {!benLid && !invites.myInvite && currentUser && !invites.myRequest && (
              <Button size="sm" variant="outline"
                      onClick={async () => {
                        try {
                          await teamsAPI.joinRequest(team.id, null)
                          window.alert('Aanvraag verstuurd naar kapiteins.')
                          laadInvites()
                        }
                        catch (e) { setErr(e.response?.data?.error ?? e.message) }
                      }}>
                <Icons.UserPlus className="h-4 w-4 mr-1" /> Vraag om erbij te komen
              </Button>
            )}
            {!benLid && invites.myRequest && (
              <Button size="sm" variant="outline" disabled
                      title="Wacht op antwoord van kapitein">
                <Icons.Clock className="h-4 w-4 mr-1" /> Aanvraag pending
              </Button>
            )}
            {kanBeheren && (
              <>
                <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={uploadBanner} />
                <Button variant="outline" size="sm" onClick={() => bannerRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> {team.banner_url ? 'Banner wijzigen' : 'Banner'}
                </Button>
                {team.banner_url && (
                  <Button variant="outline" size="sm" onClick={wisBanner} title="Banner verwijderen">
                    Banner weg
                  </Button>
                )}
              </>
            )}
            {kanBeheren && (
              <Button variant="outline" size="sm"
                      onClick={async () => {
                        if (!inventaris) {
                          try { const r = await profileItemsAPI.ofTeam(team.id); setInventaris(r.data) } catch {}
                        }
                        setLoadoutOpen(o => !o)
                      }}>
                Pimp team
              </Button>
            )}
            {kanBeheren && (
              <Button asChild variant="outline" size="sm">
                <Link to="/teams"><Edit3 className="h-4 w-4 mr-1" /> Bewerk in Teams</Link>
              </Button>
            )}
            {benLid && !kanBeheren && (
              <Button variant="outline" size="sm" onClick={() => wisLid(currentUser.id)}>
                <LogOut className="h-4 w-4 mr-1" /> Verlaat
              </Button>
            )}
          </div>
        }
      />

      {kanBeheren && loadoutOpen && inventaris && (
        <TeamLoadout
          items={inventaris.items}
          equipped={inventaris.equipped}
          onEquip={async (slot, itemId) => {
            try {
              await profileItemsAPI.equipTeam(team.id, slot, itemId)
              const r = await profileItemsAPI.ofTeam(team.id); setInventaris(r.data)
              laad()
            } catch (e) { window.alert(e.response?.data?.error ?? e.message) }
          }}
          onSluit={() => setLoadoutOpen(false)}
        />
      )}

      <div className="mt-4">
        <StatStrip accent={accent} items={[
          { label: 'Leden',        value: members.length, icon: Users,  color: accent },
          { label: 'Compo goud',   value: goud,           icon: Crown,  color: '#E4C265' },
          { label: 'Zilver',       value: zilver,         icon: Trophy, color: '#C0C0C0' },
          { label: 'Brons',        value: brons,          icon: Trophy, color: '#CD7F32' },
        ]} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          {compo.length > 0 && <TrophyHall results={compo} />}
          <MatchLog results={compo} />
        </div>

        <div className="pf-hud space-y-4">
          {kanBeheren && invites.requests.length > 0 && (
            <div>
              <SectionHeader label="Aanvragen" count={invites.requests.length} />
              <div className="space-y-1.5">
                {invites.requests.map(r => (
                  <div key={r.id} className="flex items-center gap-3 rounded-[4px] border border-border bg-card px-3 py-2">
                    <Icons.UserPlus className="h-4 w-4 text-acid shrink-0" />
                    <div className="flex-1 min-w-0">
                      {/* Klikbare naam → profiel opent in nieuw tabblad zodat de
                          kapitein z'n plek in de aanvragen-lijst niet kwijt is. */}
                      <Link to={`/spelers/${r.user_id}`} target="_blank" rel="noopener noreferrer"
                            className="text-sm font-semibold truncate block hover:text-acid underline decoration-dotted"
                            title="Open profiel in nieuw tabblad">
                        {r.display_name || r.username}
                      </Link>
                      {r.message && <div className="text-xs text-muted-foreground truncate">{r.message}</div>}
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                            title="Goedkeuren"
                            onClick={async () => { await teamsAPI.respondInvite(r.id, true); laad(); laadInvites() }}>
                      <Icons.Check className="h-4 w-4 text-live" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                            title="Weigeren"
                            onClick={async () => { await teamsAPI.respondInvite(r.id, false); laadInvites() }}>
                      <Icons.X className="h-4 w-4 text-signal" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {kanBeheren && (
            <InviteBox teamId={teamId}
                       members={members}
                       onSent={() => { laad(); laadInvites() }} />
          )}
          <SectionHeader label="Roster" count={members.length} />
          {members.length === 0 ? (
            <p className="pf-mono text-xs text-muted-foreground">Nog geen leden.</p>
          ) : (
            <div className="space-y-1.5">
              {members.map(m => (
                <div key={m.membership_id}
                     className="flex items-center gap-3 rounded-[6px] border bg-card px-3 py-2 relative"
                     style={{ borderColor: m.role === 'captain' ? `${accent}66` : 'var(--border)' }}>
                  <div className="h-9 w-9 rounded-full bg-muted grid place-items-center shrink-0 pf-name text-sm"
                       style={{ color: accent }}>
                    {(m.display_name || m.username || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/spelers/${m.user_id}`}
                          className="text-sm font-semibold truncate hover:text-acid transition-colors block">
                      {m.display_name || m.username}
                    </Link>
                    <div className="pf-mono text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
                      @{m.username}
                      {m.role === 'captain' && (
                        <span className="inline-flex items-center gap-0.5" style={{ color: accent }}>
                          <Crown className="h-2.5 w-2.5" /> kapitein
                        </span>
                      )}
                    </div>
                  </div>
                  {kanBeheren && (
                    <>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => wisselKapitein(m.user_id, m.role)}
                              title={m.role === 'captain' ? 'Kapitein-rol wegnemen' : 'Maak kapitein'}>
                        <Crown className={`h-3.5 w-3.5 ${m.role === 'captain' ? 'text-gold' : 'text-muted-foreground'}`} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => wisLid(m.user_id)}>
                        <Icons.X className="h-3.5 w-3.5 text-signal" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Uitnodig-box voor kapitein/admin ────────────────────────
   Zoek in users, filter degenen die al lid zijn, klik = invite versturen.
   Debounced typing zodat we niet elke toets een API-call doen. */
function InviteBox({ teamId, members, onSent }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [busyId, setBusyId] = useState(null)
  const [status, setStatus] = useState(null)  // {type: 'ok'|'err', text}

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) { setResults([]); return }
    const t = setTimeout(() => {
      authAPI.searchUsers(query.trim())
        .then(r => setResults(r.data?.users || r.data || []))
        .catch(() => setResults([]))
    }, 250)
    return () => clearTimeout(t)
  }, [query])

  const isLid = (uid) => members.some(m => m.user_id === uid)

  const nodigUit = async (u) => {
    setBusyId(u.id); setStatus(null)
    try {
      await teamsAPI.invite(teamId, u.id, '')
      setStatus({ type: 'ok', text: `${u.display_name || u.username} uitgenodigd` })
      setQuery(''); setResults([])
      onSent?.()
    } catch (e) {
      setStatus({ type: 'err', text: e.response?.data?.error || e.message })
    } finally { setBusyId(null) }
  }

  return (
    <div className="mb-4">
      <SectionHeader label="Speler uitnodigen" />
      <div className="space-y-1.5">
        <Input placeholder="Zoek op naam of gebruikersnaam…"
               value={query} onChange={e => setQuery(e.target.value)}
               className="h-8 text-sm" />
        {results.length > 0 && (
          <div className="space-y-1 max-h-48 overflow-y-auto rounded-[4px] border border-border bg-card">
            {results.slice(0, 10).map(u => {
              const already = isLid(u.id)
              return (
                <div key={u.id} className="flex items-center gap-2 px-2 py-1.5 border-b border-border last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{u.display_name || u.username}</div>
                    <div className="pf-mono text-[10px] text-muted-foreground truncate">@{u.username}</div>
                  </div>
                  {already ? (
                    <span className="pf-mono text-[10px] uppercase text-muted-foreground">lid</span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7"
                            disabled={busyId === u.id}
                            onClick={() => nodigUit(u)}>
                      <Icons.UserPlus className="h-3 w-3 mr-1" />Nodig uit
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {status && (
          <div className={`text-xs ${status.type === 'ok' ? 'text-live' : 'text-signal'}`}>
            {status.text}
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Team-loadout paneel — analoog aan de user-versie ──────── */

const SLOT_LABEL = { banner: 'Banner', avatar_frame: 'Kader op logo', accent: 'Accent-kleur' }
// Rarity-tokens via @/lib/rarity — proxy voor drop-in compat met bestaande callsite.
const RARITY_KLEUR = new Proxy({}, { get: (_, r) => rarityColor(r) })

function TeamLoadout({ items, equipped, onEquip, onSluit }) {
  const perSlot = { banner: [], avatar_frame: [], accent: [] }
  for (const it of items) if (perSlot[it.kind]) perSlot[it.kind].push(it)
  const eqId = {
    banner: equipped.equipped_banner_id,
    avatar_frame: equipped.equipped_frame_id,
    accent: equipped.equipped_accent_id,
  }
  return (
    <div className="mt-4 rounded-[6px] border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="pf-name text-lg">Pimp dit team</div>
          <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Kapiteins + admins kiezen wat het team draagt
          </div>
        </div>
        <button onClick={onSluit} className="text-sm text-muted-foreground hover:text-foreground">Sluiten</button>
      </div>
      {['banner', 'avatar_frame', 'accent'].map(slot => (
        <div key={slot}>
          <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            {SLOT_LABEL[slot]}
          </div>
          {perSlot[slot].length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Nog niks in dit slot. Admin kan items toewijzen via <Link to="/admin/profile-items" className="text-acid underline">profielitems</Link>.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => onEquip(slot, null)}
                      className={`px-3 py-2 rounded-[4px] border text-sm ${eqId[slot] == null ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                — Geen —
              </button>
              {perSlot[slot].map(it => {
                const gekozen = eqId[slot] === it.id
                const rk = RARITY_KLEUR[it.rarity] || RARITY_KLEUR.common
                return (
                  <button key={it.id} onClick={() => onEquip(slot, it.id)}
                          className={`px-3 py-2 rounded-[4px] border text-sm text-left ${gekozen ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}
                          style={{ borderLeft: `3px solid ${rk}` }}>
                    <div className="font-semibold">{it.name}</div>
                    {it.source_hint && (
                      <div className="pf-mono text-[10px] uppercase tracking-widest" style={{ color: rk }}>
                        {it.rarity} · {it.source_hint}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
